import { action, computed, observable } from 'mobx';

import { ViewController } from '@estee/elc-service';

import { SignUpType } from '~constants/SignUpTypeEnum';
import { ConfigRepository } from '~repositories/ConfigRepository';
import { MessageGroup, MessageRepository } from '~repositories/MessageRepository';
import { UserRepository } from '~repositories/UserRepository';
import { OtpCheckedUseCase } from '~use-cases/OtpCheckedUseCase';
import { RampSignUpFormLoadedUseCase } from '~use-cases/RampSignUpFormLoadedUseCase';
import { RedirectedToSignInPageUseCase } from '~use-cases/RedirectedToSignInPageUseCase';
import { SignUpSubmittedUseCase } from '~use-cases/SignUpSubmittedUseCase';
import { SocialProfileLoadedUseCase } from '~use-cases/SocialProfileLoadedUseCase';
import { emitAnalyticsEvent, eventTag, SignUpTaggingEventType } from '~utils/Analytics';
import { getHideSocialPositionWrapper, getSocialProviderFromStorage } from '~utils/SocialUtils';

interface ISignUpViewController {
    userRepository: UserRepository;
    configRepository: ConfigRepository;
    messageRepository: MessageRepository;
}

export class SignUpViewController extends ViewController {
    private userRepository: UserRepository;
    private configRepository: ConfigRepository;
    private messageRepository: MessageRepository;

    @observable public isScrolled: boolean = true;
    @observable public firstErrorDomElement: HTMLElement;

    constructor({ userRepository, configRepository, messageRepository }: ISignUpViewController) {
        super();

        this.userRepository = userRepository;
        this.configRepository = configRepository;
        this.messageRepository = messageRepository;
    }

    @computed
    public get data() {
        const {
            otpStore: { data: { showOtp } = {} },
            onlineSignUpFormStore,
            triageAlertState,
            isSignInToConnect
        } = this.userRepository;
        const {
            configNodeName,
            formSchema,
            socialProviders,
            excludedFromSocialLogin,
            iamConfig: { additionalTermsLabelKey, fieldsValidations = {} } = {},
            dataConfig: { showLoginForm }
        } = this.configRepository;
        const errorMessages = this.messageRepository.messages.get(MessageGroup.SIGN_UP);
        const hideSocialButtons =
            excludedFromSocialLogin ||
            getHideSocialPositionWrapper(socialProviders?.providers, isSignInToConnect);

        return {
            configNodeName,
            formSchema,
            fieldsValidations,
            formStore: this.formStore,
            onlineSignUpFormStore,
            showOtp,
            errorMessages,
            validationObject: this.formStore.errors,
            socialConfig: socialProviders,
            additionalTermsLabelKey,
            hideSocialButtons,
            showLoginForm,
            triageAlertState
        };
    }

    @action
    public loaded = async () => {
        const { isSocialProfile } = this.configRepository;
        if (isSocialProfile) {
            await new SocialProfileLoadedUseCase().execute();
        } else {
            await new RampSignUpFormLoadedUseCase().execute();
        }
    };

    @action
    public submitButtonClicked = async (type: SignUpType) => {
        const {
            signUpEventSource,
            userFormStore: { validateForm, isValid },
            consentForm: { isVerified, isSocialSignUpFlow }
        } = this.userRepository;
        validateForm();
        this.isScrolled = false;
        if (isValid) {
            const {
                otpConfig,
                isSocialProfile,
                iamConfig: { identifiers = [], turnOffOtp } = {}
            } = this.configRepository;
            // TODO: APACRIGEL-2052 redirect to SocialConsentSubmittedUseCase directly?
            const submit = new SignUpSubmittedUseCase(type).execute;

            const isVerifiedSource =
                isSocialSignUpFlow && isVerified(otpConfig?.otpType, identifiers);
            if (otpConfig && !isSocialProfile && !isVerifiedSource && !turnOffOtp) {
                await new OtpCheckedUseCase(otpConfig.otpType, 'signup', type, submit).execute();
            } else {
                await submit();
            }
        } else {
            emitAnalyticsEvent(eventTag.SIGN_UP_FAILED, {
                source: signUpEventSource,
                provider: getSocialProviderFromStorage() || 'standard',
                type: SignUpTaggingEventType.VALIDATION_FAILURE
            });
        }
    };

    @computed
    protected get formStore() {
        return this.userRepository.userFormStore;
    }

    public signInClicked = () => {
        new RedirectedToSignInPageUseCase().execute();
    };

    @computed
    public get shouldScroll(): boolean {
        const {
            errorMessages,
            triageAlertState: { visible }
        } = this.data;

        return !this.isScrolled && (!this.formStore?.isValid || !!errorMessages || visible);
    }

    @action
    public scrollComplete = () => {
        this.isScrolled = true;
    };
}
